// Angular imports
import { Component, OnInit, Input, ViewChild, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';

// 3rd party imports
import { DxDropDownBoxComponent, DxDataGridComponent, DxTextBoxComponent } from 'devextreme-angular';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { HelperService } from '../../../../_services/shared-services/helper.service';

// Model imports
import { CommunitySelectorResultDto } from '../../../../_models/community-selector-result-dto';

@Component({
    selector: 'app-grid-cell-multi-column-selector',
    templateUrl: './grid-cell-multi-column-selector.component.html',
    styleUrls: ['./grid-cell-multi-column-selector.component.scss']
})
export class GridCellMultiColumnSelectorComponent implements OnInit, OnChanges {
    @ViewChild('dropDownBox', { static: false }) dropdownBox: DxDropDownBoxComponent;
    @ViewChild('dropDownBoxTextBox', { static: false }) dropDownBoxTextBox: DxTextBoxComponent;
    @ViewChild('dataGrid', { static: false }) dataGrid: DxDataGridComponent;

    @Input() placeholder = 'Select a value...';
    @Input() gridSource: any;
    @Input() displayExpr: string;
    @Input() valueExpr: string;

    @Input() cellInfo: any;
    @Input() boxValue: any[];

    @Input() columnDef: any[];
    @Input() name: string;
    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() dropDownButtonTemplate: any;
    @Input() dropDownOptions: object;
    @Input() isItemSelectionMandatory = false;
    @Input() showClearButton = true;
    @Input() cssClass = 'drop-down-box';
    @Input() wordWrapEnabled = false;
    @Input() avoidDefaultSort = false;
    @Input() navigateToCommunity = false;
    @Input() dropDownInputAttr: object = {};
    @Input() isRequired = false;
    @Input() validationCallback: (opt: any) => boolean;
    @Input() validationGroup: string;

    @Output() addTemplateEvent = new EventEmitter<any>();
    @Output() editTemplateEvent = new EventEmitter<any>();
    @Output() gridSelectionEvent = new EventEmitter<any>();
    @Output() refreshSourceEvent = new EventEmitter<any>();
    @Output() detailTemplateEvent = new EventEmitter<any>();
    @Output() keyPressedEvent = new EventEmitter<any>();
    @Output() onSelectorFocusIn = new EventEmitter<any>();
    @Output() cellInfoSelectionEvent = new EventEmitter<any>(); // ANG-2704, Emits the cell data information of the grid when a value is selected from the dropdown

    searchText: string;
    textInputVal: string;
    focusedRowIndex = -1;
    isEditEnabled = false;
    selectedItem: any = null;
    inlineButtonTemplate: any[] = [];
    dropdownButtonList: any[] = [];
    buttonTemplate: string;
    elementAttr: object = {};

    iconProduction = IconConstants.PRODUCTION;
    iconTransition = IconConstants.IN_TRANSITION;
    iconTerminate = IconConstants.TERMINATE;
    iconExclamatory = IconConstants.EXCLAMATION_CIRCLE;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;

    constructor(private readonly helperService: HelperService) {
        this.searchText = '';
        this.textInputVal = null;
        this.selectedItem = null;
        this.onNavigateToCommunity = this.onNavigateToCommunity.bind(this);
        this.customValidationCallback = this.customValidationCallback.bind(this);
    }

    ngOnInit() {
        this.textInputVal = null;
        this.selectedItem = null;
        this.elementAttr = { class: this.cssClass };

        this.setSelectedRowText();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.gridSource && changes.gridSource.currentValue) {
            if (this.avoidDefaultSort === false) {
                this.gridSource.sort((ele1, ele2) => {
                    const name1 = ele1[this.displayExpr].toLowerCase();
                    const name2 = ele2[this.displayExpr].toLowerCase();
                    if (name1 >= name2) {
                        return 1;
                    }
                    if (name1 < name2) {
                        return -1;
                    }
                    return 0;
                });
            }

            this.isElementSelected();
        }

        if (changes.boxValue && changes.boxValue.currentValue) {
            this.setSelectedRowText();
        }

        if (changes.cellInfo && changes.cellInfo.currentValue) {
            this.boxValue = [this.cellInfo.value];
            this.setSelectedRowText();
        }

        if (changes.columnDef && changes.columnDef.currentValue) {
            this.configureColumnsSearchEditor();
        }

        if (changes.dropDownButtonTemplate && changes.dropDownButtonTemplate.currentValue) {
            this.setDropdownButtonList();
            this.getDropdownButtonTemplate();
        }
    }

    getStatus(rowData: CommunitySelectorResultDto, status: string): string {
        const date = this.helperService.getDateString(rowData.terminatingDate);
        return Utils.isTrue(rowData.isTerminating) && Utils.notEqualsIgnoreCase(rowData.status, 'Terminated')
            ? `Community will terminate on ${date}`
            : status;
    }

    onNavigateToCommunity(): void {
        if (!!this.selectedItem) {
            this.helperService.openDefaultCommunityPageInNewWindow(this.selectedItem[this.valueExpr]);
        }
    }

    onDropdownOpen(event: any): void {
        if (!!this.selectedItem && this.dataGrid && this.dataGrid.instance) {
            this.searchText = '';
            setTimeout(() => this.dataGrid.instance.navigateToRow(this.selectedItem[this.valueExpr]), 100);
        }
    }

    onDropdownClose(event: any): void {
        if (!!this.selectedItem && Utils.notEquals(this.selectedItem[this.valueExpr], this.boxValue[0])) {
            this.textInputVal = this.selectedItem[this.displayExpr];
            this.boxValue = [this.selectedItem[this.valueExpr]];
            this.cellInfo.setValue(this.selectedItem[this.valueExpr]);
        }
    }

    onTextFocusIn(event: any): void {
        if (!this.dropdownBox.instance.option('opened')) {
            this.dropdownBox.instance.open();
            setTimeout(() => {
                if (typeof event.event.target.select === 'function') {
                    event.event.target.select();
                }
            }, 100);
        }
    }

    onTextFocusOut(event: any): void {
        if (!this.dropdownBox.instance.option('opened')) {
            this.dropdownBox.instance.close();
            setTimeout(() => {
                if (typeof event.event.target.blur === 'function') {
                    event.event.target.blur();
                }
            }, 100);
        }
    }

    onTextKeyUp(event: any): void {
        if (!this.readOnly && !!event && !!event.event) {
            if (event.event.key.toLowerCase() === 'enter') {
                if (this.focusedRowIndex === -1) {
                    this.dropdownBox.instance.open();
                } else if (this.focusedRowIndex >= 0) {
                    this.dataGrid.instance.selectRowsByIndexes([this.focusedRowIndex]);
                    this.dropdownBox.instance.close();
                } else if (this.searchText === '') {
                    this.clearSelection();
                }
            } else if (event.event.target.value.trim() !== this.searchText) {
                this.searchText = event.event.target.value.trim();
                this.focusedRowIndex = -1;
            }
        }
    }

    onTextKeyDown(event: any): void {
        if (!this.readOnly && !!event && !!event.event) {
            if (event.event.key.toLowerCase() === 'tab') {
                if (this.focusedRowIndex >= 0) {
                    this.dataGrid.instance.selectRowsByIndexes([this.focusedRowIndex]);
                    this.dropdownBox.instance.close();
                } else if (this.searchText === '') {
                    this.clearSelection();
                }
                this.onTextFocusOut(event);
                this.keyPressedEvent.emit(event);
            } else if (event.event.key.toLowerCase() === 'escape') {
                this.onTextFocusOut(event);
            } else {
                if (event.event.key.toLowerCase() !== 'shift') {
                    this.onTextFocusIn(event);
                }
            }
            if (event.event.key.toLowerCase() === 'arrowdown') {
                this.focusedRowIndex += 1;
            } else if (event.event.key.toLowerCase() === 'arrowup' && this.focusedRowIndex > 0) {
                this.focusedRowIndex -= 1;
            }
        }
    }

    onValueChanged(event: any): void {
        if ((!event.value || !event.value.length) && !!this.selectedItem) {
            this.clearSelection();
        } else {
            this.searchText = event.value.trim();
            this.textInputVal = this.searchText;
            setTimeout(() => this.dropDownBoxTextBox.instance.focus(), 100);
        }
    }

    onSelectionChanged(event: any): void {
        if (event.selectedRowKeys.length) {
            this.boxValue = [event.selectedRowKeys];
            if (!this.boxValue || (this.boxValue.length > 0 && event.selectedRowKeys[0] !== this.boxValue[0])) {
                this.boxValue = event.selectedRowKeys;
                this.cellInfo.setValue(this.boxValue[0]);
                this.raiseGridSelectionEvent();
                this.cellInfoSelectionEvent.emit(this.cellInfo);
            }
        }
    }

    onRowClick(event: any): void {
        this.dropdownBox.instance.close();
    }

    onTemplateButtonClick(event, button): void {
        const pointerEvent = event.event;
        pointerEvent.stopPropagation();
        if (button.name === 'clear') {
            this.clearSelection();
        } else if (button.name === 'addIcon') {
            this.addTemplateEvent.emit();
        } else if (button.name === 'edit' || button.name === 'editIcon') {
            this.editTemplateEvent.emit();
        } else if (button.name === 'refresh') {
            this.refreshSourceEvent.emit();
        } else if (button.name === 'detail') {
            this.detailTemplateEvent.emit();
        }
    }

    customValidationCallback(opt: any): boolean {
        let isValid = Utils.isFalse(this.isRequired) || Utils.isTrue(this.readOnly);
        if (
            Utils.isFalse(isValid) &&
            (Utils.notNullOrEmpty(this.searchText) || Utils.notNullAndDefined(this.selectedItem))
        ) {
            isValid = Utils.notNullAndDefined(opt.value);
            if (Utils.notNullAndDefined(this.validationCallback)) {
                isValid = this.validationCallback(opt);
            }
        }
        return isValid;
    }

    onDropdownFocusIn(e: any): void {
        this.dropDownBoxTextBox.instance.focus();
        this.onSelectorFocusIn.emit(e);
    }

    private clearSelection(): void {
        this.resetSelector();
        this.gridSelectionEvent.emit(false);
    }

    private isElementSelected(): void {
        if (this.isItemSelectionMandatory) {
            this.isEditEnabled = false;
            setTimeout(() => {
                if (this.gridSource && this.gridSource.length > 0 && this.boxValue && this.boxValue.length > 0) {
                    if (this.selectedItem) {
                        this.isEditEnabled = true;
                    } else {
                        this.isEditEnabled = false;
                    }
                }
            }, 200);
        } else {
            setTimeout(() => (this.isEditEnabled = true), 200);
        }
    }

    private setSelectedRowText(): void {
        if (this.gridSource && this.gridSource.length > 0 && this.boxValue && this.boxValue.length > 0) {
            const selectedRows = this.gridSource.filter((x) => x[this.valueExpr] === this.boxValue[0]);
            if (selectedRows.length > 0) {
                if (
                    Utils.isNullOrUndefined(this.selectedItem) ||
                    Utils.notEquals(this.selectedItem[this.valueExpr], this.boxValue[0])
                ) {
                    this.selectedItem = selectedRows[0];
                    this.textInputVal = this.selectedItem[this.displayExpr];
                    this.searchText = this.textInputVal;
                    this.isElementSelected();
                    const selectedRowIndex = this.gridSource.indexOf(this.selectedItem);
                    const selectedObject = { selectedRow: selectedRows, selectedRowIndex };
                    this.gridSelectionEvent.emit(selectedObject);
                }
            }
        } else {
            this.selectedItem = null;
            this.textInputVal = null;
            this.searchText = '';
        }
    }

    private getDropdownButtonTemplate(): void {
        this.inlineButtonTemplate = [];
        this.buttonTemplate = 'dropDownArrayButton';
        if (this.dropDownButtonTemplate === 'dropDownEditButton') {
            this.inlineButtonTemplate = this.dropdownButtonList.filter((d) => d.name === 'edit' || d.name === 'clear');
            this.isElementSelected();
        } else if (typeof this.dropDownButtonTemplate !== 'string' && this.dropDownButtonTemplate.length > 0) {
            this.dropDownButtonTemplate.forEach((element) => {
                if (element === 'dropDownEditButton') {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'edit'));
                }
                if (element === 'dropDownEditIconButton') {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'editIcon'));
                }
                if (element === 'dropDownAddIconButton') {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'addIcon'));
                }
                if (element === 'dropDownRefreshButton') {
                    this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'refresh'));
                }
                if (element === 'dropDownDetailButton') {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'detail'));
                }
            });
            this.inlineButtonTemplate.push(this.dropdownButtonList.find((d) => d.name === 'clear'));
        } else {
            this.inlineButtonTemplate = [];
            this.buttonTemplate = 'dropDownButton';
        }
    }

    private setDropdownButtonList(): void {
        this.dropdownButtonList = [
            {
                name: 'addIcon',
                text: null,
                icon: IconConstants.ADD,
                class: 'select-normal-button',
                type: 'default'
            },
            {
                name: 'editIcon',
                text: null,
                icon: IconConstants.EDIT,
                class: 'select-default-button',
                type: 'default'
            },
            {
                name: 'edit',
                text: this.name + ' Edit',
                icon: null,
                class: 'select-default-button',
                type: 'default'
            },
            {
                name: 'refresh',
                text: null,
                type: 'normal',
                icon: IconConstants.REFRESH,
                class: 'select-normal-button'
            },
            {
                name: 'clear',
                text: null,
                type: 'normal',
                icon: 'clear',
                class: 'select-normal-button'
            },
            {
                name: 'detail',
                text: 'Details',
                icon: IconConstants.INFO,
                class: 'select-default-button',
                type: 'default'
            }
        ];
    }

    private configureColumnsSearchEditor(): void {
        this.columnDef.forEach((col) => {
            if (!!col.editorOptions) {
                col.editorOptions.placeholder = '';
            }
        });
    }

    private raiseGridSelectionEvent(): void {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData();
        const selectedRowIndex = this.dataGrid.instance.getRowIndexByKey(this.boxValue[0]);

        if (selectedRow.length > 0) {
            this.selectedItem = selectedRow[0];
            this.textInputVal = this.selectedItem[this.displayExpr];
            this.searchText = this.textInputVal;
        } else {
            this.textInputVal = null;
            this.selectedItem = null;
            this.searchText = '';
        }
        this.isElementSelected();
        const selectedObject = { selectedRow, selectedRowIndex };
        if (selectedRow.length > 0) {
            this.gridSelectionEvent.emit(selectedObject);
        } else {
            this.gridSelectionEvent.emit(false);
        }

        setTimeout(() => {
            this.dropdownBox.instance.close();
            this.dropDownBoxTextBox.instance.blur();
        }, 100);
    }

    public resetSelector(): void {
        this.searchText = '';
        this.boxValue = null;
        this.cellInfo.setValue(null);
        this.textInputVal = null;
        this.focusedRowIndex = -1;
        this.selectedItem = null;
        this.isEditEnabled = false;
        if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.clearFilter();
            this.dataGrid.instance.clearSelection();
        }

        this.isElementSelected();
    }

    public setComponentFocus(): void {
        this.dropdownBox.instance.focus();
        this.dropDownBoxTextBox.instance.focus();
    }

    public removeComponentFocus(): void {
        this.dropdownBox.instance.close();
    }

    public setCellValue(value: any): void {
        this.boxValue = [value];
        this.cellInfo.setValue(this.boxValue[0]);
        this.raiseGridSelectionEvent();
    }
}
